// Pestaña Bitácora: el audit trail de la venta (`GET /backoffice/sales/{id}/log`), un evento por renglón.
// Si el registro no responde o viene vacío, cae a los hechos que guarda la propia venta.
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { CopyableId } from '@/kit';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import type { BackofficeSale, SaleLogEntry } from '@/api/services/retail-wizard/contract';
import { ACTOR_WITHOUT_NAME_NOTE } from './CancellationSection';
import { TAB_BODY, TabNote, TabSection, dash, formatFechaHora } from './VentaTab';

/** Lo mínimo que la bitácora necesita de la venta — así el tab se testea sin armar el DTO entero. */
export type BitacoraSale = Pick<
    BackofficeSale,
    'soldAt' | 'cancelledAt' | 'cancelReason' | 'customerName' | 'retailCustomerId' | 'creditId'
>;

interface Evento {
    key: string;
    titulo: string;
    at: string;
    detalle?: string;
    tone: 'ok' | 'bad';
}

/**
 * Etiquetas de las acciones auditadas que hoy escribe el silo.
 *
 * La acción desconocida NO se esconde ni se renombra: se muestra su código crudo. Una acción nueva
 * del backend tiene que aparecer aunque nadie haya escrito su etiqueta todavía — ocultarla dejaría
 * un hueco silencioso justo en el registro que existe para no tener huecos.
 */
const ACTION_LABEL: Record<string, string> = {
    SALE_COMPLETE: 'Venta cerrada',
    SALE_CANCEL: 'Venta cancelada',
    PAYMENT_REGISTERED: 'Pago registrado',
    SALE_AMENDED: 'Venta corregida',
};

/** Las acciones que se pintan en rojo — las que quitan o revierten algo. */
const BAD_ACTIONS = new Set(['SALE_CANCEL']);

function fromAuditTrail(entries: SaleLogEntry[]): Evento[] {
    return entries.map((entry, i) => ({
        key: `audit-${String(i)}`,
        titulo: ACTION_LABEL[entry.action] ?? entry.action,
        at: entry.at,
        detalle: entry.reason ?? undefined,
        tone: BAD_ACTIONS.has(entry.action) ? 'bad' : 'ok',
    }));
}

/** El fallback: lo que la venta sabe de sí misma. */
function fromSale(
    sale: BitacoraSale,
    t: (k: string, o: { defaultValue: string }) => string,
): Evento[] {
    const eventos: Evento[] = [
        {
            key: 'cerrada',
            titulo: t('bitacora.cerrada', { defaultValue: 'Venta cerrada' }),
            at: sale.soldAt,
            tone: 'ok',
        },
    ];
    if (sale.cancelledAt) {
        eventos.push({
            key: 'cancelada',
            titulo: t('bitacora.cancelada', { defaultValue: 'Venta cancelada' }),
            at: sale.cancelledAt,
            detalle: dash(sale.cancelReason),
            tone: 'bad',
        });
    }
    return eventos;
}

export function BitacoraTab({
    sale,
    log,
    logFailed = false,
}: {
    sale: BitacoraSale;
    /** El audit trail. `undefined` mientras carga o si falló. */
    log?: SaleLogEntry[];
    logFailed?: boolean;
}) {
    const { t } = useTranslation('ventas');

    const auditado = log !== undefined && log.length > 0;
    const eventos = auditado ? fromAuditTrail(log) : fromSale(sale, t);

    return (
        <div className={TAB_BODY}>
            <section>
                <h3 className="mb-2 text-[11.5px] font-semibold">
                    {t('bitacora.titulo', { defaultValue: 'Eventos de la venta' })}
                </h3>
                <ol className="mb-3">
                    {eventos.map((e) => (
                        <li
                            key={e.key}
                            className="flex gap-2 border-b border-border py-1.5 last:border-b-0"
                        >
                            <span
                                aria-hidden
                                className={cn(
                                    'mt-[5px] size-1.5 flex-none rounded-full',
                                    e.tone === 'bad' ? 'bg-danger' : 'bg-success',
                                )}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="text-[11.5px] leading-snug">
                                    {e.titulo}
                                    {e.detalle === undefined ? null : (
                                        <span className="text-muted-foreground">
                                            {' · '}
                                            {e.detalle}
                                        </span>
                                    )}
                                </div>
                                <div className="font-mono text-[10px] text-muted-foreground">
                                    {formatFechaHora(e.at)} ·{' '}
                                    <span title={ACTOR_WITHOUT_NAME_NOTE} className="cursor-help">
                                        —
                                    </span>
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            </section>

            <TabSection title={t('bitacora.referencias', { defaultValue: 'Referencias cruzadas' })}>
                <div className="flex flex-col gap-1 text-[11.5px]">
                    <div>
                        <span className="text-muted-foreground">
                            {t('bitacora.cliente', { defaultValue: 'Cliente' })}:{' '}
                        </span>
                        {sale.retailCustomerId ? (
                            <Link
                                to="/retail-admin/$section"
                                params={{ section: 'clientes' }}
                                search={{ cliente: sale.retailCustomerId }}
                                className="text-primary hover:underline"
                            >
                                {dash(saleCustomerLabel(sale))}
                            </Link>
                        ) : (
                            saleCustomerLabel(sale)
                        )}
                    </div>
                    {sale.creditId === null ? null : (
                        <CopyableId
                            label={t('detalle.credito', { defaultValue: 'Crédito' })}
                            value={sale.creditId}
                        />
                    )}
                </div>
            </TabSection>

            {auditado ? null : (
                <TabNote>
                    {logFailed
                        ? t('bitacora.limiteError', {
                              defaultValue:
                                  'No se pudo leer la bitácora auditada. Lo de arriba es lo que la venta registra de sí misma, no el registro completo.',
                          })
                        : t('bitacora.limite', {
                              defaultValue:
                                  'La venta no tiene entradas en el registro de auditoría todavía. Lo de arriba son los hechos que ella misma guarda.',
                          })}
                </TabNote>
            )}
        </div>
    );
}
